import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { Fragment, useCallback, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, useHistory, withRouter } from "react-router-dom";

import { route } from "@/configs/route.configs";
import { logOut } from "@/reducers/mrgReducer/mrgThunks";
import { isNotSet, isSet } from "@/tools/format.checker";

import { SwaggerAPI } from "@/api/hunger.api";
import ConditionalFragment from "@/components/General/ConditionalFragment";
import { useUserProfileContext } from "@/context/UserProfileContext";
import { TokenTypes } from "@/types/token.type";
import { faStore } from "@fortawesome/pro-regular-svg-icons";
import { useAsync } from "react-use";
import { ReactComponent as Logo } from "../../../assets/icon/hun-com-logo-desktop-white-img.svg";
import Modal from "../../General/Modal/Modal";
import classNames from "classnames";
import { AxiosError } from "axios";

type ReduxProps = ConnectedProps<typeof connector>;

type OwnProps = { onClose: () => void; showCloseButton?: boolean };

type Props = ReduxProps & OwnProps & RouteComponentProps;

const GroupSelectModal: React.FC<Props> = ({
  logOut,
  onClose,
  showCloseButton,
}) => {
  const [showPermissionDeniedModal, setShowPermissionInvalidModal] =
    useState<boolean>(false);
  const { getCurrentGroupUser } = useUserProfileContext();
  const history = useHistory();

  const { value: groups } = useAsync(async () => {
    const { data } = await SwaggerAPI.getGroups();
    return data;
  }, []);

  const onLogOutClick = useCallback(() => {
    logOut();
    onClose();
    history.push(route.login.to());
  }, [history, logOut, onClose]);

  const onCardClick = useCallback(
    async (groupId: number) => {
      try {
        const { data } = await SwaggerAPI.switchGroup({ groupId: groupId });
        localStorage.setItem(TokenTypes.ACCESS_TOKEN, data.token);
        getCurrentGroupUser();
        onClose();
        history.push(route.dashboard.path);
      } catch (error) {
        const axiosErr = error as AxiosError;

        if (
          axiosErr.isAxiosError &&
          axiosErr.response?.data.name === "PERMISSION_INVALID"
        ) {
          toggleShowModal();
        } else {
          alert(error);
        }
      }
    },
    [history, getCurrentGroupUser, onClose]
  );

  const toggleShowModal = useCallback(() => {
    setShowPermissionInvalidModal((prev) => !prev);
  }, []);

  return (
    <Fragment>
      <ConditionalFragment condition={showPermissionDeniedModal}>
        <PermissionDeniedModal onClose={toggleShowModal} />
      </ConditionalFragment>
      <Modal>
        <div className="select_group">
          <header className="select_group__header">
            <Logo className="select_group__logo" />
            <ConditionalFragment condition={showCloseButton}>
              <button onClick={onClose} className="select_group__close">
                <FontAwesomeIcon icon={["far", "times"]} />
              </button>
            </ConditionalFragment>
          </header>
          <main className="select_group__container">
            <h2 className="heading__2 select_group__heading">選擇品牌</h2>
            <ul className="select_group__list">
              {groups?.map(({ id, logoPhotoFile, name }) => {
                const imgUrl = logoPhotoFile?.url;
                return (
                  <li
                    onClick={() => onCardClick(id)}
                    key={id}
                    className="card select_group__item"
                  >
                    <span className="select_group__img">
                      <ConditionalFragment condition={isSet(imgUrl)}>
                        <img src={imgUrl} alt="Group" className={"h-full"} />
                      </ConditionalFragment>
                      <ConditionalFragment condition={isNotSet(imgUrl)}>
                        <span className="select_group__img--icon">
                          <FontAwesomeIcon icon={faStore} />
                        </span>
                      </ConditionalFragment>
                    </span>
                    <span className="select_group__name">{name}</span>
                    <span className="select_group__go">
                      <FontAwesomeIcon icon={["fas", "chevron-right"]} />
                    </span>
                  </li>
                );
              })}
            </ul>
            <button
              onClick={onLogOutClick}
              className="btn btn__sign_out select_group__btn"
            >
              登出
            </button>
          </main>
        </div>
      </Modal>
    </Fragment>
  );
};

const mapDispatchToProps = {
  logOut,
};

const connector = connect(null, mapDispatchToProps);

export default connector(withRouter(GroupSelectModal));

const PermissionDeniedModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  return (
    <Modal>
      <div
        className={classNames(
          "w-[16rem]",
          "bg-white",
          "rounded-xl",
          "overflow-hidden"
        )}
      >
        <h3
          className={classNames(
            "w-full",
            "py-6",
            "text-2xl text-center",
            "bg-grey-5"
          )}
        >
          無管理權限
        </h3>
        <p className={"p-6 text-grey-2 text-center"}>
          {"此帳號無管理權限，請聯絡你的品牌擁有者。"}
        </p>
        <div
          className={classNames(
            "flex justify-center align-center",
            "px-4 pt-2 pb-6"
          )}
        >
          <button
            onClick={onClose}
            className={classNames(
              "w-full",
              "py-2",
              "mr-2",
              "text-white",
              "bg-primary-pink",
              "rounded-full border"
            )}
          >
            關閉
          </button>
        </div>
      </div>
    </Modal>
  );
};
